import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form, Button } from "react-bootstrap";
import { message } from "antd";
import { usePlanificationSettings } from "../hooks/usePlanificationSettings";
import SettingsLayout from "../components/SettingsLayout";
import CustomButton from "components/Common/ui/CustomButton";
import { LocalizedTimePicker } from "components/Common/ui/LocalizedDatePicker";
import dayjs from "dayjs";
import { normalizeTime } from "utils/dateUtils";

const PlanificationSettings = () => {
  const { t } = useTranslation();
  const {
    planificationConfig: planificationConfigState,
    setPlanificationConfig,
    loading,
    error,
    savePlanificationSettings,
  } = usePlanificationSettings();

  const listDays = [
    { tag: "monday", label: t("monday"), value: "0" },
    { tag: "tuesday", label: t("tuesday"), value: "1" },
    { tag: "wednesday", label: t("wednesday"), value: "2" },
    { tag: "thursday", label: t("thursday"), value: "3" },
    { tag: "friday", label: t("friday"), value: "4" },
    { tag: "saturday", label: t("saturday"), value: "5" },
  ];

  useEffect(() => {
    if (!planificationConfigState.days) {
      setPlanificationConfig((prev) => ({
        ...prev,
        days: [],
      }));
    }
  }, []);

  const handleDayChange = (index: number, field: string, checked: boolean) => {
    const selectedDay = listDays[index];
    let updatedDays = [...(planificationConfigState.days || [])];

    if (checked) {
      updatedDays.push({ label: selectedDay.tag, value: selectedDay.value });
    } else {
      updatedDays = updatedDays.filter((day) => day.label !== selectedDay.tag);
    }

    updatedDays.sort((a, b) => Number(a.value) - Number(b.value));
    setPlanificationConfig({
      ...planificationConfigState,
      days: updatedDays,
    });
  };

  const handleTimeSlotChange = (
    index: number,
    field: string,
    value: string | dayjs.Dayjs | null
  ) => {
    let timeValue = "";
    if (value) {
      if (typeof value === "string") {
        timeValue = value.includes(":") ? value : `${value}:00`;
      } else if (dayjs.isDayjs(value)) {
        timeValue = value.format("HH:mm:ss");
      }
    }

    const updatedTimeSlots = [...planificationConfigState.timeSlots];
    const currentSlot = updatedTimeSlots[index];
    const updatedSlot = {
      ...currentSlot,
      [field]: timeValue,
    };

    const newLabel = formatTimeSlotLabel(
      field === "start" ? timeValue : currentSlot.start,
      field === "end" ? timeValue : currentSlot.end
    );

    updatedSlot.label = newLabel;
    updatedTimeSlots[index] = updatedSlot;

    setPlanificationConfig({
      ...planificationConfigState,
      timeSlots: updatedTimeSlots,
    });
  };

  const formatTimeSlotLabel = (start: string, end: string) => {
    const format = (t: string) => t.slice(0, 5); // Get hour part
    return `${format(start)}-${format(end)}`;
  };

  const addNewTimeSlot = () => {
    if (planificationConfigState.timeSlots.length >= 4) {
      message.warning(t("validation.maximum_4_time_slots_allowed"));
      return;
    }

    const newTimeSlot = {
      start: "08:00:00",
      end: "10:00:00",
      label: formatTimeSlotLabel("08:00:00", "10:00:00"),
    };

    setPlanificationConfig({
      ...planificationConfigState,
      timeSlots: [...planificationConfigState.timeSlots, newTimeSlot],
    });
  };

  const removeTimeSlot = (index: number) => {
    const updatedTimeSlots = planificationConfigState.timeSlots.filter(
      (_, i) => i !== index
    );
    const relabeledTimeSlots = updatedTimeSlots.map((slot, i) => ({
      ...slot,
      label: formatTimeSlotLabel(slot.start, slot.end),
    }));
    setPlanificationConfig({
      ...planificationConfigState,
      timeSlots: relabeledTimeSlots,
    });
  };

  const verifyPlanificationConfig = () => {
    const activeDays = planificationConfigState.days;

    if (activeDays.length === 0) {
      message.error(t("validation.please_select_at_least_one_day"));
      return false;
    }

    if (planificationConfigState.timeSlots.length === 0) {
      message.error(t("validation.please_add_at_least_one_time_slot"));
      return false;
    }

    if (
      planificationConfigState.timeSlots.some((slot) => slot.start >= slot.end)
    ) {
      message.error(t("validation.start_time_must_be_before_end_time"));
      return false;
    }

    if (
      planificationConfigState.timeSlots.some((slot) => slot.start === slot.end)
    ) {
      message.error(t("validation.start_time_and_end_time_cannot_be_the_same"));
      return false;
    }

    for (let i = 0; i < planificationConfigState.timeSlots.length; i++) {
      for (let j = i + 1; j < planificationConfigState.timeSlots.length; j++) {
        const slot1 = planificationConfigState.timeSlots[i];
        const slot2 = planificationConfigState.timeSlots[j];
        if (slot1.start === slot2.start && slot1.end === slot2.end) {
          message.error(t("validation.duplicate_time_slots_not_allowed"));
          return false;
        }
      }
    }

    const slots = planificationConfigState.timeSlots;
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        const slot1 = slots[i];
        const slot2 = slots[j];

        const toMinutes = (timeStr: any) => {
          const [hours, minutes] = timeStr.split(':').map(Number);
          return hours * 60 + minutes;
        };

        const start1 = toMinutes(slot1.start);
        const end1 = toMinutes(slot1.end);
        const start2 = toMinutes(slot2.start);
        const end2 = toMinutes(slot2.end);

        if (start1 === start2 || (start1 < end2 && start2 < end1)) {
          message.error(
            t("validation.overlapping_time_slots_not_allowed", {
              number1: i + 1,
              number2: j + 1
            })
          ); return false;
        }
      }
    }

    return true;
  };


  const sortTimeSlotsByStart = (slots: any[]) => {
    return [...slots].sort((a, b) => {
      return a.start.localeCompare(b.start);
    });
  };

  const handleSubmit = async () => {
    if (!verifyPlanificationConfig()) return;
    const normalizedSlots = sortTimeSlotsByStart(
      planificationConfigState.timeSlots.map((slot) => {
        const start = normalizeTime(slot.start);
        const end = normalizeTime(slot.end);
        return {
          ...slot,
          start,
          end,
          label: formatTimeSlotLabel(start, end),
        };
      })
    );

    const payload = {
      ...planificationConfigState,
      timeSlots: normalizedSlots,
    };

    try {
      await savePlanificationSettings({
        planningConfiguration: payload,
      });
    } catch (error) {
      console.error("Error updating planification config:", error);
    }
  };

  return (
    <SettingsLayout titleKey="planification_settings" loading={loading}>
      <Form>
        <div className="mb-4">
          <h5 className="mb-3">{t("days")}</h5>
          {listDays.map((day, index) => (
            <div key={index} className="card p-3 mb-3">
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label={day.label}
                  checked={
                    !!planificationConfigState?.days?.some(
                      (d) => d.label === day.tag
                    )
                  }
                  onChange={(e) =>
                    handleDayChange(index, "value", e.target.checked)
                  }
                />
              </Form.Group>
            </div>
          ))}
        </div>

        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>{t("time_slots")}</h5>
            <Button
              variant="outline-primary"
              onClick={addNewTimeSlot}
              disabled={planificationConfigState?.timeSlots?.length >= 4}
            >
              <i className="fas fa-plus me-2"></i>
              {t("add_time_slot")}
            </Button>
          </div>
          {planificationConfigState?.timeSlots?.map((slot, index) => (
            <div key={index} className="card p-3 mb-3">
              <div className="d-flex align-items-start">
                <div className="flex-grow-1">
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label>{t("start_time")}</Form.Label>
                        <LocalizedTimePicker
                          value={slot.start ? dayjs(slot.start, "HH:mm:ss") : null}
                          onChange={(time) =>
                            handleTimeSlotChange(index, "start", time)
                          }
                          format="HH:mm"
                          className="w-100"
                          style={{ width: "100%" }}
                          disabledTime={() => ({
                            disabledHours: () => {
                              const allowed = [8, 9, 10, 11, 12, 14, 15, 16, 17, 18];
                              return Array.from({ length: 24 }, (_, hour) => hour).filter(hour => !allowed.includes(hour))
                            },
                            disabledMinutes: (selectedHour) => {
                              const allowed = [0, 15, 30, 45];
                              return Array.from({ length: 60 }, (_, minute) => minute).filter(minute => !allowed.includes(minute))
                            }
                          })}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label>{t("end_time")}</Form.Label>
                        <LocalizedTimePicker
                          value={slot.end ? dayjs(slot.end, "HH:mm:ss") : null}
                          onChange={(time) =>
                            handleTimeSlotChange(index, "end", time)
                          }
                          format="HH:mm"
                          className="w-100"
                          style={{ width: "100%" }}
                          disabledTime={() => ({
                            disabledHours: () => {
                              const allowed = [8, 9, 10, 11, 12, 14, 15, 16, 17, 18];
                              return Array.from({ length: 24 }, (_, hour) => hour).filter(hour => !allowed.includes(hour))
                            },
                            disabledMinutes: (selectedHour) => {
                              const allowed = [0, 15, 30, 45];
                              return Array.from({ length: 60 }, (_, minute) => minute).filter(minute => !allowed.includes(minute))
                            }
                          })}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </div>
                <Button
                  variant="link"
                  className="text-danger ms-3"
                  onClick={() => removeTimeSlot(index)}
                >
                  <i className="fas fa-trash"></i>
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-end">
          <CustomButton variant="primary" onClick={handleSubmit} className="px-5">
            <i className="fas fa-save me-2"></i>
            {t("save_changes")}
          </CustomButton>
        </div>
      </Form>
    </SettingsLayout>
  );
};

export default PlanificationSettings;